// poiesis/src/api/http.ts
// Single auth + fetch layer for the monad/Aion API. ONE source of the caller
// identity, threaded through ONE fetch wrapper — no per-method duplication of
// headers or the dev fallback id.
//
// AUTH (SEN-1336 A2): two schemes, chosen per request in ONE place:
//  1. A stored session (src/auth/session.ts) → `Authorization: Bearer <token>`
//     and NO X-Member-Id. A 401 on such a request means the token is dead:
//     the session is cleared and `poiesis:unauthorized` fires → sign-in.
//  2. No session → the rc.1 `X-Member-Id` dev fallback (build-time
//     VITE_MEMBER_ID, the TenantSwitcher's persisted choice, or the built-in
//     bench member). Kept so every existing dev/test flow works untouched.
//     VITE_REQUIRE_LOGIN=1 (prod/preprod builds) disables EVERY header fallback:
//     no built-in id, no VITE_MEMBER_ID, no localStorage switcher id — the
//     browser never sends X-Member-Id; a session is the only way in.
import { getSession, clearSession, notifyUnauthorized } from '../auth/session'

const DEV_MEMBER_ID = 'e9d2a471-279e-475d-b569-ffc6350b3de4'

// DEV tenant-switcher (SEN-685 stopgap): the selected tenant's member id, persisted
// in localStorage by the sidebar TenantSwitcher. Takes precedence over the build-time
// VITE_MEMBER_ID so switching tenants re-auths as a member of the chosen tenant.
export const DEV_MEMBER_KEY = 'monad.dev.memberId'
export const DEV_SLUG_KEY = 'monad.dev.slug'

function env(): Record<string, string | undefined> {
  // Read import.meta.env directly (not through a cast) so vite's static
  // replacement and vitest's vi.stubEnv() both see the same object.
  return (import.meta.env ?? {}) as unknown as Record<string, string | undefined>
}

/** True when the build requires a real login: NO X-Member-Id fallback of any kind. */
export function loginRequired(): boolean {
  return env().VITE_REQUIRE_LOGIN === '1'
}

/** The dev-fallback member id, or null when no fallback is available (login required). */
export function devMemberIdOrNull(): string | null {
  if (loginRequired()) return null
  try {
    const m = localStorage.getItem(DEV_MEMBER_KEY)
    if (m) return m
  } catch {
    /* localStorage may throw (privacy mode) — fall through to the env/default */
  }
  return env().VITE_MEMBER_ID ?? DEV_MEMBER_ID
}

/** Forget the TenantSwitcher's persisted dev member id (stale after a DB re-seed,
 *  and never to outlive a real login/logout). */
export function clearDevMemberId(): void {
  try { localStorage.removeItem(DEV_MEMBER_KEY) } catch { /* ignore */ }
}

/** True when a request can be authenticated WITHOUT a session (X-Member-Id path). */
export function hasDevFallback(): boolean {
  return devMemberIdOrNull() !== null
}

/** The active tenant slug: a signed-in session's tenant wins, then the
 *  TenantSwitcher's persisted choice, then build-time VITE_TENANT_SLUG, then 'bench'.
 *  One source, shared by App + switcher + sign-in. */
export function tenantSlug(): string {
  const hosted = httpHost?.slug?.()
  if (hosted) return hosted
  const s = getSession()
  if (s?.tenant_slug) return s.tenant_slug
  // ?tenant=<slug> deep-links straight into a tenant (CEO 2026-08-20:
  // 'straight to jysk') — it persists like the TenantSwitcher, then the
  // param keeps winning for the life of the URL. A signed-in session still
  // takes precedence (the dev override never beats real auth).
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('tenant')
    if (fromUrl) {
      persistTenantSlug(fromUrl)
      return fromUrl
    }
  } catch { /* no window (tests) — fall through */ }
  try {
    const stored = localStorage.getItem(DEV_SLUG_KEY)
    if (stored) return stored
  } catch { /* localStorage may throw — fall through */ }
  return env().VITE_TENANT_SLUG ?? 'bench'
}
/** @deprecated alias kept for existing imports — use tenantSlug(). */
export const devSlug = tenantSlug

/** Persist the tenant slug the same way the TenantSwitcher does (one key). */
export function persistTenantSlug(slug: string): void {
  try { localStorage.setItem(DEV_SLUG_KEY, slug) } catch { /* privacy mode */ }
}

export interface AuthedInit extends RequestInit {
  /** Send NO identity header (login / accept-invite are unauthenticated). */
  anonymous?: boolean
}

/**
 * EMBEDDING seam (Sentinel's Work screen, 2026-10-21): an app that mounts
 * Poiesis components (CollectionTable, RecordPage) inside its own shell owns
 * the NomOS session itself. It hands Poiesis that session's bearer token and
 * the API root here; every request then rides that identity instead of
 * Poiesis's own stored session (and a 401 is the host's to handle). The
 * tenant needs no seam: every component takes its tenant `slug` as a prop.
 * Unset (Poiesis itself): unchanged.
 */
export interface HttpHost {
  /** The host session's bearer token, or null when signed out. */
  token(): string | null
  /** The NomOS root the `/v1/…` paths are relative to ('' = same origin). */
  baseUrl?: string
  /** The host's active tenant (tenantSlug() answers it): a cell widget that resolves names
   *  (a record filter's sites) has no slug prop of its own. */
  slug?: () => string | null
}
let httpHost: HttpHost | null = null
export function configureHttp(host: HttpHost | null): void {
  httpHost = host
}

/** The bearer identity a coalesced read travels with (api/batch.ts): the
 *  embedding host's session, else Poiesis's own. None (a dev member-id
 *  header): reads are not batched — they go alone through authedFetch. */
export function batchIdentity(): { baseUrl: string; token: string } | null {
  if (httpHost) {
    const token = httpHost.token()
    return token ? { baseUrl: httpHost.baseUrl ?? '', token } : null
  }
  const session = getSession()
  return session ? { baseUrl: '', token: session.token } : null
}

/** fetch() with the caller identity attached (the one auth point). */
export async function authedFetch(path: string, init: AuthedInit = {}): Promise<Response> {
  const { anonymous, headers, ...rest } = init
  if (httpHost) {
    const token = anonymous ? null : httpHost.token()
    const url = /^https?:/.test(path) ? path : `${httpHost.baseUrl ?? ''}${path}`
    return fetch(url, { ...rest, headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(headers as Record<string, string> | undefined) } })
  }
  const session = anonymous ? null : getSession()
  const identity: Record<string, string> = {}
  if (!anonymous) {
    if (session) identity.Authorization = `Bearer ${session.token}`
    else {
      const id = devMemberIdOrNull()
      if (id) identity['X-Member-Id'] = id
    }
  }
  const res = await fetch(path, { ...rest, headers: { ...identity, ...(headers as Record<string, string> | undefined) } })
  if (res.status === 401 && !anonymous) {
    // Bearer rejected → the session is dead. Header fallback rejected → the
    // persisted dev member id is stale (re-seeded DB) or the server wants a
    // real login: drop the stale id so a reload can fall back to env/default,
    // and let the gate show sign-in.
    if (session) clearSession()
    else clearDevMemberId()
    notifyUnauthorized()
  }
  return res
}

import { unwrapV1, unwrapV1WithMeta, type PlenumMeta } from './envelope'
import { batchedRead, type RawResponse } from './batch'

/** THE coalesced read (api/batch.ts) for every POST read route NomOS runs
 *  inside a batch — a question (`/queries`), records/query, records/values,
 *  records/chips. Reads asked together (a dashboard's tiles, a record page's
 *  widgets) travel as ONE POST /v1/…/batch, each answered exactly as its own
 *  request would be. With no bearer identity (the dev member-id header) the
 *  read goes alone through authedFetch, as before. One seam, so no caller
 *  re-implements the 401 re-ask or the envelope parse. */
async function readThroughBatch(path: string, init: RequestInit): Promise<{ status: number; json: unknown }> {
  const identity = batchIdentity()
  const send = async (): Promise<RawResponse> => {
    const res = await authedFetch(path, init)
    return { ok: res.ok, status: res.status, text: await res.text(), url: res.url }
  }
  const raw = identity
    ? await batchedRead({ baseUrl: identity.baseUrl, tenant: tenantOfPath(path), token: identity.token,
        method: String(init.method ?? 'GET'), path, payload: typeof init.body === 'string' ? init.body : undefined, send })
    : await send()
  // a refused credential inside a batch is re-asked alone, so the one auth point sees it
  const final = raw.status === 401 && identity ? await send() : raw
  let json: unknown = null
  try { json = final.text ? JSON.parse(final.text) : null } catch { json = null }
  return { status: final.status, json }
}

/** `/v1/tenants/{t}/…` → t (decoded); '' when the path names no tenant (never batched). */
function tenantOfPath(path: string): string {
  const m = /^\/v1\/tenants\/([^/?]+)/.exec(path)
  return m ? decodeURIComponent(m[1]) : ''
}

/** fetchV1 for a READ, through the read coalescer (see readThroughBatch). */
export async function readV1<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { status, json } = await readThroughBatch(path, init)
  return unwrapV1<T>(status, json)
}
/** readV1 with the envelope's meta (pagination, warnings). */
export async function readV1WithMeta<T>(path: string, init: RequestInit = {}): Promise<{ data: T; meta: PlenumMeta }> {
  const { status, json } = await readThroughBatch(path, init)
  return unwrapV1WithMeta<T>(status, json)
}

/** A STREAMED v1 read (`Accept: application/x-ndjson`, the /v1/batch line
 *  convention: one JSON object per line, in completion order): every line goes
 *  to `onLine` as it arrives. Never coalesced — a batch buffers an item's body
 *  whole, which is exactly what a stream exists to avoid. A response that is
 *  not a stream (an error envelope, a server that answers JSON) is returned as
 *  `{ status, json }` for the caller to unwrap; a stream returns null. */
export async function streamV1Lines(
  path: string, init: RequestInit, onLine: (line: unknown) => void,
): Promise<{ status: number; json: unknown } | null> {
  const res = await authedFetch(path, {
    ...init, headers: { ...(init.headers as Record<string, string> | undefined), Accept: 'application/x-ndjson' },
  })
  const ctype = res.headers.get('content-type') ?? ''
  if (!res.ok || !ctype.includes('ndjson')) {
    return { status: res.status, json: await res.json().catch(() => null) }
  }
  const take = (text: string) => { if (text.trim()) onLine(JSON.parse(text)) }
  if (res.body && typeof res.body.getReader === 'function') {
    const reader = res.body.getReader()
    const dec = new TextDecoder()
    let buf = ''
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buf += dec.decode(value, { stream: true })
      let nl: number
      while ((nl = buf.indexOf('\n')) >= 0) { take(buf.slice(0, nl)); buf = buf.slice(nl + 1) }
    }
    take(buf + dec.decode())
  } else {
    (await res.text()).split('\n').forEach(take)
  }
  return null
}

/** Envelope-aware fetch for migrated v1 routes. Unwraps {data, meta}; throws PlenumApiError on {error, meta}. */
export async function fetchV1<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authedFetch(path, init)
  return unwrapV1<T>(res.status, await res.json().catch(() => null))
}
/** Same seam, but returns meta too (getMrl needs meta.source_snapshot + meta.pagination). */
export async function fetchV1WithMeta<T>(path: string, init: RequestInit = {}): Promise<{ data: T; meta: PlenumMeta }> {
  const res = await authedFetch(path, init)
  return unwrapV1WithMeta<T>(res.status, await res.json().catch(() => null))
}
